import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  SimpleChanges,
} from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { Subject, Subscription } from "rxjs";
import { debounceTime } from "rxjs/operators";
import { FormItem, FormListType } from "./hd-form.service";
import {
  getCascaderMenuClassName,
  getCascaderMenuStyle,
  getCascaderShowSearch,
} from "../utils/cascader.util";
// import * as moment from 'moment';
@Component({
  selector: "hd-form",
  templateUrl: "./hd-form.component.html",
  styleUrls: ["./hd-form.component.less"],
})
export class HdFormComponent implements OnInit {
  @Input() formList: Array<FormItem>;
  @Output() changeEvent = new EventEmitter();
  validateHdForm: FormGroup;
  inputValue: string;

  private searchSubject = new Subject<{ fn: any; event: any }>();
  private debounceTimeout: number = 0; // 默认防抖时间
  private searchSubscription: Subscription | undefined;

  private openChangeSubject = new Subject<{ fn: any; event: any }>();
  private openChangeDebounceTimeout: number = 0;
  private openChangeSubscription: Subscription | undefined;

  constructor(private fb: FormBuilder) {
    this.searchSubscription = this.searchSubject
      .pipe(debounceTime(this.debounceTimeout))
      .subscribe(({ fn, event }) => this.triggerEvent(fn, event));
    this.openChangeSubscription = this.openChangeSubject
      .pipe(debounceTime(this.openChangeDebounceTimeout))
      .subscribe(({ fn, event }) => {
        // 防抖结束后仅展开态触发业务回调，避免频繁开关仍打接口
        if (event) {
          this.triggerEvent(fn, event);
        }
      });
  }

  ngOnInit() {
    if (!this.formList || this.formList.length === 0) {
      // 模板始终绑定 [formGroup]，空列表时也必须有 FormGroup 实例
      this.validateHdForm = this.fb.group({});
      return;
    }

    this.init();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      changes["formList"] &&
      JSON.stringify(changes["formList"].currentValue) !==
        JSON.stringify(changes["formList"].previousValue)
    ) {
      if (!this.formList || this.formList.length === 0) {
        this.validateHdForm = this.fb.group({});
        return;
      }
      this.init();
    }
  }

  onSearchEvent(fn: any, event: any, onSearchEventDebounceTime: any) {
    if (!fn) {
      return;
    }
    const debounceMs =
      onSearchEventDebounceTime != null ? onSearchEventDebounceTime : 0;
    // 未配置或为 0：同步触发，不走 debounceTime（避免异步）
    if (!debounceMs) {
      this.triggerEvent(fn, event);
      return;
    }
    this.debounceTimeout = debounceMs;
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }
    this.searchSubscription = this.searchSubject
      .pipe(debounceTime(this.debounceTimeout))
      .subscribe(({ fn, event }) => this.triggerEvent(fn, event));
    this.searchSubject.next({ fn, event });
  }

  onOpenChangeEvent(
    fn: any,
    open: boolean,
    onOpenChangeEventDebounceTime: any,
    formItem?: any,
  ) {
    if (!fn) {
      return;
    }
    // 展开时先清空旧 options，避免条件变化后请求返回前仍展示上一批下拉数据
    if (open) {
      this.clearSelectOptionsOnOpen(formItem);
    }
    const debounceMs =
      onOpenChangeEventDebounceTime != null
        ? onOpenChangeEventDebounceTime
        : 0;
    // 未配置或为 0：同步触发，不走 debounceTime（避免异步）
    if (!debounceMs) {
      if (open) {
        this.triggerEvent(fn, open);
      }
      return;
    }
    this.openChangeDebounceTimeout = debounceMs;
    if (this.openChangeSubscription) {
      this.openChangeSubscription.unsubscribe();
    }
    this.openChangeSubscription = this.openChangeSubject
      .pipe(debounceTime(this.openChangeDebounceTimeout))
      .subscribe(({ fn, event }) => {
        if (event) {
          this.triggerEvent(fn, event);
        }
      });
    this.openChangeSubject.next({ fn, event: open });
  }

  /** 有 onOpenChangeEvent 时展开下拉先清空选项，由业务回调再写入新数据 */
  private clearSelectOptionsOnOpen(formItem: any): void {
    if (!formItem || !formItem.selectOption) {
      return;
    }
    const opt = formItem.selectOption;
    if (opt.selectList != null) {
      opt.selectList = [];
    }
  }

  triggerEvent(fn: any, event: any) {
    if (fn) {
      fn(event);
    }
  }

  private init(): void {
    // 异常捕获
    if (!this.checkList(this.formList)) {
      throw "form组件数据格式不规范";
    }
    // RegionAddress 固定占两列，保证级联宽与单列控件对齐
    this.formList.forEach((item) => {
      if (item.type === FormListType.RegionAddress) {
        item.width = 2;
      }
    });
    // 对表单数据做初始化，用row隔离每行数据item
    this.dealRow();
    // 首先根据定义的组件数组  去初始化表单
    let formGroupList = {};
    this.formList.forEach((item, index) => {
      // 日期相关的控件做特殊处理
      if (item.type === 2 && item.value) {
        const [year, month, day] = item.value
          .toString()
          .split("-")
          .map((str) => parseInt(str));
        const newDateStr = new Date(year, month - 1, day)
          .toLocaleDateString("zh-Hans-CN", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
          })
          .replace(/\//g, "-");
        if (item.validator) {
          formGroupList[item.name] = this.fb.control(
            { value: new Date(newDateStr), disabled: item.disabled },
            item.require && !item.hide
              ? [Validators.required, item.validator]
              : [],
          );
        } else {
          formGroupList[item.name] = this.fb.control(
            { value: new Date(newDateStr), disabled: item.disabled },
            item.require && !item.hide ? [Validators.required] : [],
          );
        }
      } else if (item.type === 3 && item.value) {
        let formItemDate = JSON.parse(JSON.stringify(item.value));
        if (formItemDate && formItemDate instanceof Array) {
          formItemDate = formItemDate.map((item) => {
            const [year, month, day] = item
              .split("-")
              .map((str) => parseInt(str));
            const newDateStr = new Date(year, month - 1, day)
              .toLocaleDateString("zh-Hans-CN", {
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
              })
              .replace(/\//g, "-");
            item = new Date(newDateStr);
          });
          if (item.validator) {
            formGroupList[item.name] = this.fb.control(
              { value: formItemDate, disabled: item.disabled },
              item.require && !item.hide
                ? [Validators.required, item.validator]
                : [],
            );
          } else {
            formGroupList[item.name] = this.fb.control(
              { value: formItemDate, disabled: item.disabled },
              item.require && !item.hide ? [Validators.required] : [],
            );
          }
        } else {
          throw "form组件中DateRange控件value值格式不规范，正确示例['2020-01-22', '2020-01-23']";
        }
      } else if (item.type === FormListType.Checkbox && item.checkboxOption) {
        const checkboxValue = Array.isArray(item.value) ? item.value : [];
        if (item.validator) {
          formGroupList[item.name] = this.fb.control(
            { value: checkboxValue, disabled: item.disabled },
            item.require && !item.hide
              ? [Validators.required, item.validator]
              : [],
          );
        } else {
          formGroupList[item.name] = this.fb.control(
            { value: checkboxValue, disabled: item.disabled },
            item.require && !item.hide ? [Validators.required] : [],
          );
        }
      } else if (item.type === FormListType.Checkbox) {
        const checkboxValue = item.value != null ? item.value : false;
        if (item.validator) {
          formGroupList[item.name] = this.fb.control(
            { value: checkboxValue, disabled: item.disabled },
            item.require && !item.hide
              ? [Validators.required, item.validator]
              : [],
          );
        } else {
          formGroupList[item.name] = this.fb.control(
            { value: checkboxValue, disabled: item.disabled },
            item.require && !item.hide ? [Validators.required] : [],
          );
        }
      } else if (item.type === FormListType.RegionAddress) {
        const regionValue = Array.isArray(item.value) ? item.value : null;
        const addressValue =
          item.addressValue != null ? item.addressValue : null;
        const requireValidators =
          item.require && !item.hide ? [Validators.required] : [];
        if (item.validator) {
          formGroupList[item.name] = this.fb.control(
            { value: regionValue, disabled: item.disabled },
            item.require && !item.hide
              ? [Validators.required, item.validator]
              : [item.validator],
          );
        } else {
          formGroupList[item.name] = this.fb.control(
            { value: regionValue, disabled: item.disabled },
            requireValidators,
          );
        }
        formGroupList[item.addressName] = this.fb.control(
          { value: addressValue, disabled: item.disabled },
          requireValidators,
        );
      } else {
        if (item.validator) {
          formGroupList[item.name] = this.fb.control(
            { value: item.value, disabled: item.disabled },
            item.require && !item.hide
              ? [Validators.required, item.validator]
              : [],
          );
        } else {
          formGroupList[item.name] = this.fb.control(
            { value: item.value, disabled: item.disabled },
            item.require && !item.hide ? [Validators.required] : [],
          );
        }
      }
    });

    this.validateHdForm = this.fb.group(formGroupList);

    this.formList.forEach((item) => {
      if (item.onChangeEvent) {
        const debounceMs =
          item.onChangeEventDebounceTime != null
            ? item.onChangeEventDebounceTime
            : 0;
        const control = this.validateHdForm.get(item.name);
        // 未配置或为 0：同步订阅，不走 debounceTime（避免异步破坏 patchValue 联动回显）
        if (debounceMs > 0) {
          control.valueChanges
            .pipe(debounceTime(debounceMs))
            .subscribe((value) =>
              this.triggerEvent(item.onChangeEvent, value),
            );
        } else {
          control.valueChanges.subscribe((value) =>
            this.triggerEvent(item.onChangeEvent, value),
          );
        }
      }
    });
    this.validateHdForm.valueChanges.subscribe((data) =>
      this.onHdFormValueChanged(data),
    );
    this.onHdFormValueChanged();
  }

  private dealRow() {
    // 一阶段 遍历数据计算相关数据
    const formListLength = this.formList.length;
    let step = 0;
    for (let i = 0; i < formListLength; i++) {
      if (!this.formList[i].hide) {
        if (i === 0) {
          this.formList[i].totalWidth = this.formList[i].width || 1;
          this.formList[i].isRowFirst = 1;
          step = step + 4;
        } else {
          // 去找到前一个非hide的控件
          for (let j = i - 1; j >= 0; j--) {
            if (this.formList[j].totalWidth) {
              this.formList[i].totalWidth =
                this.formList[j].totalWidth + (this.formList[i].width || 1);
              if (
                this.formList[j].totalWidth + (this.formList[i].width || 1) >
                  step &&
                this.formList[j].totalWidth <= step
              ) {
                step = step + 4;
                this.formList[i].isRowFirst = 1;
              } else {
                this.formList[i].isRowFirst = 0;
              }
              break;
            }
          }
        }
      }
    }

    // 二阶段 isRowFirst的数组项增加子节点
    for (let m = 0; m < formListLength; m++) {
      if (this.formList[m].isRowFirst === 1) {
        this.formList[m].columnItems = [];
        let space = 0;
        for (let n = 1; n < formListLength - m; n++) {
          if (space === 4 || this.formList[m + n].isRowFirst === 1) {
            break;
          }
          if (
            !this.formList[m + n].hide &&
            this.formList[m + n] &&
            this.formList[m + n].isRowFirst === 0
          ) {
            this.formList[m].columnItems.push(this.formList[m + n]);
            space++;
          }
        }
      }
    }
  }

  private onHdFormValueChanged(data?: any) {
    this.changeEvent.emit(this.validateHdForm);
  }

  private checkList(list: Array<FormItem>): boolean {
    if (!list || (list && list.length === 0)) {
      return false;
    }
    let nameList = [];
    list.forEach((item) => {
      // 校验单项选择器
      // if (item.type === FormListType.Select && (!item.selectOption.selectList || (item.selectOption.selectList && item.selectOption.selectList.length === 0))) {
      //   throw 'form组件数据格式不规范，存在单项选择器没有传入选项!'
      // }
      if (item.type === FormListType.RegionAddress && !item.addressName) {
        throw "form组件 RegionAddress 必须配置 addressName";
      }
      if (
        item.type === FormListType.RegionAddress &&
        (!item.cascaderOption || !item.cascaderOption.options)
      ) {
        throw "form组件 RegionAddress 必须配置 cascaderOption.options";
      }
      nameList.push(item.name);
      if (item.type === FormListType.RegionAddress) {
        nameList.push(item.addressName);
      }
    });
    // 校验 name / addressName 是否重复
    if ([...new Set(nameList)].length !== nameList.length) {
      throw "form组件数据格式不规范，存在重复name值!";
    }
    // ...more check
    return true;
  }

  /** RegionAddress 双控件任一脏且无效时显示 error */
  getRegionAddressValidateStatus(item: FormItem): string {
    if (!this.validateHdForm) {
      return "";
    }
    const region = this.validateHdForm.get(item.name);
    const address = item.addressName
      ? this.validateHdForm.get(item.addressName)
      : null;
    const invalidDirty = (control) =>
      control && control.dirty && control.invalid;
    if (invalidDirty(region) || invalidDirty(address)) {
      return "error";
    }
    return "";
  }

  getRegionAddressErrorTip(item: FormItem): string {
    if (!this.validateHdForm) {
      return "";
    }
    const region = this.validateHdForm.get(item.name);
    const address = item.addressName
      ? this.validateHdForm.get(item.addressName)
      : null;
    if (region && region.dirty && region.hasError("required")) {
      return item.label + "不能为空";
    }
    if (address && address.dirty && address.hasError("required")) {
      return "详细地址不能为空";
    }
    if (item.validatorKeys && region) {
      for (let i = 0; i < item.validatorKeys.length; i++) {
        const key = item.validatorKeys[i];
        if (region.hasError(key.key)) {
          return key.label;
        }
      }
    }
    return item.label + "不能为空";
  }

  public submitForm(): void {
    Object.values(this.validateHdForm.controls).forEach((control) => {
      control.markAsDirty();
      control.updateValueAndValidity();
    });
    this.changeEvent.emit(this.validateHdForm.getRawValue());
  }

  public resetForm(): void {
    this.validateHdForm.reset();
    Object.values(this.validateHdForm.controls).forEach((control) => {
      control.markAsDirty();
      control.updateValueAndValidity();
    });
    this.changeEvent.emit(this.validateHdForm.getRawValue());
  }

  public inputClean(filter: FormItem): void {
    const patchObject = {};
    patchObject[filter.name] = null;
    this.validateHdForm.patchValue(patchObject);
  }

  /**
   * 是否允许清空：优先 FormItem.hdAllowClear，其次 selectOption.hdAllowClear，默认 true
   */
  isAllowClear(item: FormItem): boolean {
    if (!item) {
      return true;
    }
    if (item.hdAllowClear != null) {
      return !!item.hdAllowClear;
    }
    if (item.selectOption && item.selectOption.hdAllowClear != null) {
      return !!item.selectOption.hdAllowClear;
    }
    return true;
  }

  isCheckboxChecked(name: string, value: any): boolean {
    const control = this.validateHdForm.get(name);
    const current = control ? control.value : null;
    return Array.isArray(current) && current.indexOf(value) !== -1;
  }

  onCheckboxItemChange(name: string, value: any, checked: boolean): void {
    const control = this.validateHdForm.get(name);
    if (!control) {
      return;
    }
    const current = Array.isArray(control.value) ? [...control.value] : [];
    const index = current.indexOf(value);
    if (checked && index === -1) {
      current.push(value);
    } else if (!checked && index !== -1) {
      current.splice(index, 1);
    }
    control.setValue(current);
    control.markAsDirty();
    control.updateValueAndValidity();
  }

  readonly inputNumberDefaultFormatter = (value: unknown) => value;
  readonly inputNumberDefaultParser = (value: string) => value;

  getCascaderShowSearch = getCascaderShowSearch;
  getCascaderMenuClassName = getCascaderMenuClassName;
  getCascaderMenuStyle = getCascaderMenuStyle;
}
